import { useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import { useAppStatus } from './api/hooks';
import { useMaintenance, setMaintenance } from './api/maintenance';
import { useGate } from './api/gate';
import { MaintenanceScreen } from './screens/MaintenanceScreen';
import { BannedScreen } from './screens/BannedScreen';
import { initAnalytics, track } from './analytics';
import { Loader, ErrorView } from './components/StateViews';
import { TabBar } from './components/TabBar';
import { HomeScreen } from './screens/HomeScreen';
import { LeaderboardScreen } from './screens/LeaderboardScreen';
import { ScheduleScreen } from './screens/ScheduleScreen';
import { TournamentDetailScreen } from './screens/TournamentDetailScreen';
import { ProfileScreen } from './screens/ProfileScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { EditProfileScreen } from './screens/EditProfileScreen';
import { LoginScreen } from './screens/auth/LoginScreen';
import { LinkTelegramScreen } from './screens/auth/LinkTelegramScreen';
import { TermsScreen } from './screens/auth/TermsScreen';
import { OnboardingScreen } from './screens/auth/OnboardingScreen';
import { RegisteredPlayersScreen } from './screens/RegisteredPlayersScreen';
import { MyTournamentsScreen } from './screens/MyTournamentsScreen';
import { TournamentResultScreen } from './screens/TournamentResultScreen';
import { LiveTournamentScreen } from './screens/LiveTournamentScreen';
import { PlayerProfileScreen } from './screens/PlayerProfileScreen';
import { FaqScreen } from './screens/FaqScreen';

function screenName(path: string): string {
  if (path === '/') return 'home';
  if (path.startsWith('/leaderboard')) return 'leaderboard';
  if (path.startsWith('/schedule')) return 'schedule';
  if (/^\/tournaments\/\d+\/players/.test(path)) return 'tournament_players';
  if (/^\/tournaments\/\d+\/results/.test(path)) return 'tournament_results';
  if (/^\/tournaments\/\d+\/live/.test(path)) return 'tournament_live';
  if (/^\/tournaments\/\d+/.test(path)) return 'tournament_detail';
  if (path.startsWith('/me/tournaments')) return 'my_tournaments';
  if (/^\/players\/\d+\/tournaments/.test(path)) return 'player_tournaments';
  if (path.startsWith('/players/')) return 'player_profile';
  if (path === '/profile') return 'profile';
  if (path.startsWith('/settings/edit')) return 'edit_profile';
  if (path.startsWith('/settings')) return 'settings';
  if (path.startsWith('/faq')) return 'faq';
  return path;
}

export function App() {
  const auth = useAuth();
  const location = useLocation();
  const maint = useMaintenance();
  const gate = useGate();
  // Poll /status only while paused (to detect recovery); otherwise just on load/focus.
  const status = useAppStatus(maint.on);

  useEffect(() => {
    initAnalytics();
  }, []);
  useEffect(() => {
    track('screen_view', { screen: screenName(location.pathname), path: location.pathname });
  }, [location.pathname]);
  // /status is authoritative (on AND off): seeds on cold load, clears on recovery.
  useEffect(() => {
    if (status.data) setMaintenance(status.data.maintenance, status.data.message);
  }, [status.data]);

  // Maintenance mode overrides everything (incl. auth) — players see the stub.
  if (maint.on) return <MaintenanceScreen message={maint.message} />;
  // Бан: любой 403 { banned: true } переключает приложение на заглушку.
  if (gate.banned) return <BannedScreen message={gate.message} />;

  if (auth.status === 'loading') return <Loader label="Загрузка…" />;
  if (auth.status === 'error') return <ErrorView message={auth.error} />;
  if (auth.status === 'login') return <LoginScreen />;
  if (auth.status === 'link-telegram') return <LinkTelegramScreen />;
  if (auth.status === 'terms') return <TermsScreen />;
  if (auth.status === 'onboarding') return <OnboardingScreen />;

  const hideTabBar =
    /^\/tournaments\/\d+/.test(location.pathname) ||
    location.pathname.startsWith('/settings') ||
    location.pathname.startsWith('/me/') ||
    location.pathname.startsWith('/players/');

  return (
    <div className="app">
      <Routes>
        <Route path="/" element={<HomeScreen />} />
        <Route path="/leaderboard" element={<LeaderboardScreen />} />
        <Route path="/schedule" element={<ScheduleScreen />} />
        <Route path="/tournaments/:id" element={<TournamentDetailScreen />} />
        <Route path="/tournaments/:id/players" element={<RegisteredPlayersScreen />} />
        <Route path="/tournaments/:id/results" element={<TournamentResultScreen />} />
        <Route path="/tournaments/:id/live" element={<LiveTournamentScreen />} />
        <Route path="/me/tournaments" element={<MyTournamentsScreen />} />
        <Route path="/players/:id" element={<PlayerProfileScreen />} />
        <Route path="/players/:id/tournaments" element={<MyTournamentsScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/settings" element={<SettingsScreen />} />
        <Route path="/settings/edit" element={<EditProfileScreen />} />
        <Route path="/faq" element={<FaqScreen />} />
        <Route path="*" element={<HomeScreen />} />
      </Routes>
      {!hideTabBar && <TabBar />}
    </div>
  );
}
